/**
 * 检查指定位置的起始码长度
 * @param dataView - 数据视图
 * @param offset - 检查位置
 * @returns 起始码长度 (4, 3, 或 0 表示未找到)
 */
export function getStartCodeLengthAt(dataView: DataView, offset: number): number {
  // 检查 4 字节起始码 0x00000001
  if (offset + 4 <= dataView.byteLength &&
      dataView.getUint8(offset) === 0x00 &&
      dataView.getUint8(offset + 1) === 0x00 &&
      dataView.getUint8(offset + 2) === 0x00 &&
      dataView.getUint8(offset + 3) === 0x01) {
    return 4;
  }
  // 检查 3 字节起始码 0x000001
  if (offset + 3 <= dataView.byteLength &&
      dataView.getUint8(offset) === 0x00 &&
      dataView.getUint8(offset + 1) === 0x00 &&
      dataView.getUint8(offset + 2) === 0x01) {
    return 3;
  }
  return 0;
}

/**
 * 从 ArrayBuffer 中解析出所有 NALU 单元
 * @param arrayBuffer - 包含 NALU 数据的 ArrayBuffer
 * @param isUplink - 是否是上行数据(默认 false)
 * @returns NALU 数组
 *
 * @example
 * ```typescript
 * // 解析 H.264 视频数据
 * const videoData: ArrayBuffer = ...; // 获取的视频数据
 * const nalus = parseNALUs(videoData);
 *
 * // 遍历所有 NALU 单元
 * nalus.forEach((nalu) => {
 *   console.log('NALU Type:', nalu.naluType);
 *   console.log('Is IDR:', nalu.isIDR);
 *   console.log('Is SPS:', nalu.isSPS);
 *   console.log('Is PPS:', nalu.isPPS);
 *   console.log('Is SEI:', nalu.isSEI);
 *
 *   if (nalu.isSEI) {
 *     console.log('SEI Payload Type:', nalu.seiPayloadType);
 *     console.log('SEI Payload:', nalu.seiPayload);
 *   }
 * });
 *
 * // 筛选特定类型的 NALU
 * const idrFrames = nalus.filter(nalu => nalu.isIDR);
 * const spsNalus = nalus.filter(nalu => nalu.isSPS);
 * const ppsNalus = nalus.filter(nalu => nalu.isPPS);
 * const seiNalus = nalus.filter(nalu => nalu.isSEI);
 * ```
 */
export function parseNALUs(arrayBuffer: ArrayBuffer, isUplink = false): NALU[] {
  const dataView = new DataView(arrayBuffer);
  const nalus: NALU[] = [];
  let offset = 0;

  while (offset < dataView.byteLength) {
    const startCodeLength = getStartCodeLengthAt(dataView, offset);

    if (startCodeLength === 0) {
      // 未找到起始码，跳过当前字节
      offset += 1;
      continue;
    }

    // 查找下一个起始码位置
    let nextStartCodeOffset = -1;
    for (let i = offset + startCodeLength; i < dataView.byteLength; i += 1) {
      if (getStartCodeLengthAt(dataView, i) > 0) {
        nextStartCodeOffset = i;
        break;
      }
    }

    // 确定当前 NALU 的结束位置
    const naluEnd = nextStartCodeOffset === -1 ? dataView.byteLength : nextStartCodeOffset;

    // 提取当前 NALU 数据(包含起始码)
    const naluLength = naluEnd - offset;
    const naluBuffer = new ArrayBuffer(naluLength);
    const naluView = new DataView(naluBuffer);

    for (let i = 0; i < naluLength; i += 1) {
      naluView.setUint8(i, dataView.getUint8(offset + i));
    }

    // 创建 NALU 对象
    nalus.push(new NALU(naluView, isUplink));

    // 移动到下一个 NALU
    offset = naluEnd;
  }

  return nalus;
}

export class NALU {
  constructor(public dataView: DataView, isUplink = false) {
    if (this.isSEI) {
      if (isUplink) {
        this.addPreventionByte();
      } else {
        this.removePreventionByte();
      }
    }
  }

  /**
   * 给 sei payload 增加防竞争字节 0x03
   * 0x000000
   * 0x000001
   * 0x000002
   * 0x000003
   * ------>
   * 0x00000300
   * 0x00000301
   * 0x00000302
   * 0x00000303
   */
  addPreventionByte() {
    const { seiPayloadStartIndex } = this;
    const seiPayloadEndIndex = this.dataView.byteLength - 2;
    const arr = [];
    let zeroCount = 0;
    for (let i = seiPayloadStartIndex; i <= seiPayloadEndIndex; i++) {
      const value = this.dataView.getInt8(i);
      switch (value) {
        case 0x00:
        case 0x01:
        case 0x02:
        case 0x03:
          if (zeroCount === 2) {
            arr.push(0x03);
            zeroCount = 0;
          }
          if (value === 0x00) {
            zeroCount += 1;
          } else {
            zeroCount = 0;
          }
          arr.push(value);
          break;
        default:
          zeroCount = 0;
          arr.push(value);
          break;
      }
    }
    // sei payload 尾字节
    arr.push(this.dataView.getInt8(this.dataView.byteLength - 1));
    const result = new DataView(new Uint8Array([
      ...new Uint8Array(this.dataView.buffer).slice(0, seiPayloadStartIndex),
      ...arr
    ]).buffer);
    this.dataView = result;
  }

  removePreventionByte() {
    const { seiPayloadStartIndex } = this;
    const seiPayloadEndIndex = this.dataView.byteLength - 1;
    const arr = [];
    let zeroCount = 0;
    for (let i = seiPayloadStartIndex; i <= seiPayloadEndIndex; i++) {
      switch (this.dataView.getInt8(i)) {
        case 0x00:
          zeroCount++;
          arr.push(this.dataView.getInt8(i));
          break;
        case 0x03:
          if (zeroCount !== 2) {
            arr.push(this.dataView.getInt8(i));
          }
          zeroCount = 0;
          break;
        default:
          arr.push(this.dataView.getInt8(i));
          zeroCount = 0;
          break;
      }
    }
    const result = new DataView(new Uint8Array([
      ...new Uint8Array(this.dataView.buffer).slice(0, seiPayloadStartIndex),
      ...arr
    ]).buffer);
    this.dataView = result;
  }

  get seiPayloadStartIndex() {
    let payloadStartIndex = 6;
    for (let i = 6; i < this.dataView.buffer.byteLength; i += 1) {
      payloadStartIndex += 1;
      if (this.dataView.getUint8(i) !== 0xff) {
        break;
      }
    }
    return payloadStartIndex;
  }

  get seiPayloadType() {
    if (!this.isSEI) {
      return null;
    }
    return this.dataView.getUint8(5);
  }

  get seiPayload() {
    if (!this.isSEI) {
      return null;
    }
    let payloadSize = 0;
    let payloadStartIndex = 6;
    for (let i = 6; i < this.dataView.buffer.byteLength; i += 1) {
      const value = this.dataView.getUint8(i);
      payloadStartIndex += 1;
      if (value === 0xff) {
        payloadSize += 0xff;
      } else {
        payloadSize += value;
        break;
      }
    }
    const arrayBuffer = new ArrayBuffer(payloadSize);
    const dataView = new DataView(arrayBuffer);
    const incrementedPayloadStartIndex = payloadStartIndex;
    for (let i = 0; i < arrayBuffer.byteLength; i += 1) {
      dataView.setInt8(i, this.dataView.getInt8(incrementedPayloadStartIndex + i));
    }
    return dataView;
  }

  /**
   * 获取 NALU 类型 (H.264)
   * 0: 未定义
   * 1: 非IDR图像的编码条带
   * 5: IDR图像的编码条带
   * 6: SEI (补充增强信息)
   * 7: SPS (序列参数集)
   * 8: PPS (图像参数集)
   */
  get naluType() {
    // 起始码之后的第一个字节的低5位是 NALU 类型
    const naluHeaderIndex = this.getStartCodeLength();
    if (naluHeaderIndex >= this.dataView.byteLength) {
      return 0;
    }
    return this.dataView.getUint8(naluHeaderIndex) & 0x1f;
  }

  /**
   * 获取起始码长度 (3 或 4 字节)
   */
  getStartCodeLength(): number {
    if (this.dataView.byteLength >= 4 &&
        this.dataView.getUint8(0) === 0x00 &&
        this.dataView.getUint8(1) === 0x00 &&
        this.dataView.getUint8(2) === 0x00 &&
        this.dataView.getUint8(3) === 0x01) {
      return 4;
    }
    if (this.dataView.byteLength >= 3 &&
        this.dataView.getUint8(0) === 0x00 &&
        this.dataView.getUint8(1) === 0x00 &&
        this.dataView.getUint8(2) === 0x01) {
      return 3;
    }
    return 0;
  }

  /**
   * 判断是否是关键帧 (IDR)
   */
  get isIDR() {
    return this.naluType === 5;
  }

  /**
   * 判断是否是 SPS
   */
  get isSPS() {
    return this.naluType === 7;
  }

  /**
   * 判断是否是 PPS
   */
  get isPPS() {
    return this.naluType === 8;
  }

  get isSEI() {
    return this.naluType === 6;
  }
}
